import { useState } from "react";
import { toast } from "react-toastify";
import { Ban, Check, ExternalLink, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import Pagination from "../../components/Pagination";
// Queue chrome lives under the web3 feature but is generic — the same borrowing
// GUSDWithdrawals already does for CopyHash.
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { StatusBadge } from "../../features/web3/forex/components/StatusBadge";
import { FormDialog } from "../../features/web3/forex/components/FormDialog";
import {
  useApproveKyc,
  useKycList,
  useRejectKyc,
  useReopenKyc,
  useDecideSupplement,
  DOC_TYPE_OPTIONS,
  REJECTION_REASONS,
  STATUS_OPTIONS,
  SUPPLEMENT_OPTIONS,
  SUPPLEMENT_REJECTION_REASONS,
} from "./api";
import { formatDateTime } from "../../lib/format";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
const labelCls =
  "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const PAGE_SIZES = [10, 15, 20, 25, 30, 50];

const DEFAULT_FILTERS = {
  page: 1,
  pageSize: 15,
  order: "desc",
  username: "",
  status: "pending",
  docType: "",
  supplement: "",
  startDate: "",
  endDate: "",
};

const COLUMNS = [
  { label: "Submitted" },
  { label: "Name" },
  { label: "Username" },
  { label: "DOB" },
  { label: "Country" },
  { label: "Role" },
  { label: "Rank" },
  { label: "Email" },
  { label: "ID Type" },
  { label: "Address Type" },
  { label: "Documents" },
  { label: "Status" },
  { label: "Supplement" },
  { label: "", align: "right", sticky: true },
];

// The two backends answer different shapes. Staging stores documents on disk and
// returns /uploads/kyc/x.png, served under /public on the same host. Production
// stores them on S3 and returns a full presigned URL. Either way the link is
// public, so a plain link into a new tab works — the browser sends no auth header.
const IMAGE_BASE = `${process.env.REACT_APP_API_BASE_URL || ""}/public`;
const imageHref = (url) =>
  url.startsWith("http") ? url : `${IMAGE_BASE}${url}`;

// Whichever images the backend actually sent, so a missing one never renders a dead
// link. Address and selfie are null on a submission made before they were asked for
// (25 Sep 2026) — those users were approved under the old rules, so say so rather
// than leave them looking incomplete.
function DocumentLinks({ row }) {
  const sides = [
    { label: "Front", url: row.frontImageUrl },
    { label: "Back", url: row.backImageUrl },
    { label: "Address", url: row.addressImageUrl },
    { label: "Selfie", url: row.selfieImageUrl },
  ].filter((s) => s.url);

  if (sides.length === 0)
    return <span className="text-muted-foreground/40">—</span>;

  const legacy = !row.addressImageUrl && !row.selfieImageUrl;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-3">
        {sides.map((s) => (
          <a
            key={s.label}
            href={imageHref(s.url)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-ginox-green hover:underline"
          >
            {s.label}
            <ExternalLink className="h-3 w-3" />
          </a>
        ))}
      </div>
      {legacy ? (
        <p className="text-[11px] text-muted-foreground/60">
          Address &amp; selfie not required at the time
        </p>
      ) : null}
    </div>
  );
}

export default function IdentityVerification() {
  // Applied filters drive the query; draft holds what the admin is still editing,
  // so a half-typed date never fires a request.
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const list = useKycList(filters);
  const approve = useApproveKyc();
  const reject = useRejectKyc();
  const reopen = useReopenKyc();
  const decide = useDecideSupplement();

  // The row being actioned, plus which dialog is open.
  const [confirming, setConfirming] = useState(null); // { row, action }
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");

  const rows = list.data?.data;
  const busy =
    approve.isPending || reject.isPending || reopen.isPending || decide.isPending;
  const supplementReject = confirming?.action === "supplement-reject";
  const reasons = supplementReject ? SUPPLEMENT_REJECTION_REASONS : REJECTION_REASONS;
  const isOther = reason === "Other";
  const rejectReason = isOther ? otherText.trim() : reason;

  // Supplements sit on approved records, so picking one clears the status filter —
  // the default status=pending would otherwise hide every one of them. Starting a
  // username search does the same, so an approved or rejected user still turns up;
  // only the first keystroke clears it, so a status picked afterwards sticks.
  const setField = (e) => {
    const { name, value } = e.target;
    setDraft((d) => ({
      ...d,
      [name]: value,
      ...(name === "supplement" && value ? { status: "" } : {}),
      ...(name === "username" && value.trim() && !d.username.trim() ? { status: "" } : {}),
    }));
  };

  const showSupplementQueue = () => {
    const next = { ...DEFAULT_FILTERS, status: "", supplement: "pending" };
    setDraft(next);
    setFilters(next);
  };

  // Any filter change restarts at page 1 — page 0 is a backend error, and a page
  // number from the old result set usually points past the new one.
  const applyFilters = () => setFilters({ ...draft, page: 1 });

  const clearFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };

  const goToPage = (page) => {
    setDraft((d) => ({ ...d, page }));
    setFilters((f) => ({ ...f, page }));
  };

  const close = () => {
    setConfirming(null);
    setReason("");
    setOtherText("");
  };

  const runApprove = async () => {
    try {
      await approve.mutateAsync(confirming.row._id);
      toast.success("KYC approved");
      close();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Approve failed");
      close();
    }
  };

  const runReject = async () => {
    try {
      await reject.mutateAsync({ id: confirming.row._id, reason: rejectReason });
      toast.success("KYC rejected");
      close();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Reject failed");
      close();
    }
  };

  const runDecide = async (decision) => {
    try {
      await decide.mutateAsync({
        id: confirming.row._id,
        decision,
        reason: decision === "rejected" ? rejectReason : undefined,
      });
      toast.success(decision === "approved" ? "Documents accepted" : "Documents rejected");
      close();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Decision failed");
      close();
    }
  };

  const runReopen = async () => {
    try {
      await reopen.mutateAsync(confirming.row._id);
      toast.success("KYC reopened and is under review");
      close();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Reopen failed");
      close();
    }
  };

  return (
    <QueueLayout
      eyebrow="KYC"
      title="Identity"
      accent="review"
      description="KYC submissions waiting on a decision. Approve or reject straight from this list."
      isFetching={list.isFetching}
      onRefresh={() => list.refetch()}
    >
      <div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div>
            <label className={labelCls}>Username</label>
            {/* Backend matches the exact username only — no partial search. */}
            <input
              name="username"
              value={draft.username}
              onChange={setField}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Exact username"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select name="status" value={draft.status} onChange={setField} className={inputCls}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Document Type</label>
            <select name="docType" value={draft.docType} onChange={setField} className={inputCls}>
              {DOC_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" name="startDate" value={draft.startDate} onChange={setField} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" name="endDate" value={draft.endDate} onChange={setField} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <select name="order" value={draft.order} onChange={setField} className={inputCls}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Supplement</label>
            <select name="supplement" value={draft.supplement} onChange={setField} className={inputCls}>
              {SUPPLEMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={draft.pageSize} onChange={setField} className={inputCls}>
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green transition-colors hover:bg-ginox-green/20"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
          >
            Clear Filters
          </button>
          <button
            type="button"
            onClick={showSupplementQueue}
            className="rounded-md border border-sky-400/50 bg-sky-400/10 px-4 py-2 text-xs font-medium text-sky-300 transition-colors hover:bg-sky-400/20"
          >
            Supplements waiting
          </button>
        </div>
      </div>

      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={COLUMNS}
            isLoading={list.isLoading}
            rows={rows}
            renderRow={(row) => (
              <tr key={row._id} className="transition-colors hover:bg-white/[0.02]">
                <Td className="whitespace-nowrap font-mono text-muted-foreground">
                  {formatDateTime(row.createdAt)}
                </Td>
                <Td className="font-medium">
                  {[row.name, row.surname].filter(Boolean).join(" ") || "—"}
                </Td>
                <Td className="font-mono">{row.username}</Td>
                {/* What the account claims, to check against the ID. Read from the
                    current profile, and often empty — the profile requires neither. */}
                <Td className="whitespace-nowrap font-mono text-muted-foreground">
                  {row.dob || "—"}
                </Td>
                <Td className="text-muted-foreground">{row.country || "—"}</Td>
                <Td className="text-muted-foreground">{row.role || "—"}</Td>
                <Td className="text-muted-foreground">{row.rank || "—"}</Td>
                <Td className="text-muted-foreground">{row.email || "—"}</Td>
                <Td className="text-muted-foreground">{row.docTypeName}</Td>
                <Td className="text-muted-foreground">{row.addressDocTypeName || "—"}</Td>
                <Td>
                  <DocumentLinks row={row} />
                </Td>
                <Td>
                  <StatusBadge status={row.status?.toUpperCase()} />
                </Td>
                {/* The proof of address + selfie an already-approved user sent
                    after the fact. Absent (not "") on rows that never had one. */}
                <Td className="text-xs text-muted-foreground">
                  {row.supplementStatus ? (
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={row.supplementStatus.toUpperCase()} />
                      {row.supplementedAt ? (
                        <p className="font-mono">{formatDateTime(row.supplementedAt)}</p>
                      ) : null}
                      {row.supplementReviewedBy ? <p>by {row.supplementReviewedBy}</p> : null}
                      {row.supplementReviewedAt ? (
                        <p className="font-mono">reviewed {formatDateTime(row.supplementReviewedAt)}</p>
                      ) : null}
                      {row.supplementReason ? (
                        <p className="max-w-[220px] whitespace-normal">{row.supplementReason}</p>
                      ) : null}
                    </div>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td align="right" sticky>
                  {row.status === "pending" ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => setConfirming({ row, action: "approve" })}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => {
                          setReason("");
                          setOtherText("");
                          setConfirming({ row, action: "reject" });
                        }}
                      >
                        <Ban className="h-3.5 w-3.5" />
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-end gap-2 text-xs text-muted-foreground">
                      {row.reviewedBy ? <p>by {row.reviewedBy}</p> : null}
                      {row.reviewedAt ? (
                        <p className="font-mono">{formatDateTime(row.reviewedAt)}</p>
                      ) : null}
                      {row.rejectionReason ? (
                        <p className="max-w-[220px] whitespace-normal">
                          {row.rejectionReason}
                        </p>
                      ) : null}
                      {!row.reviewedBy && !row.rejectionReason ? "—" : null}
                      {row.supplementStatus === "pending" ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() => setConfirming({ row, action: "supplement-approve" })}
                          >
                            <Check className="h-3.5 w-3.5" />
                            Accept documents
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => {
                              setReason("");
                              setOtherText("");
                              setConfirming({ row, action: "supplement-reject" });
                            }}
                          >
                            <Ban className="h-3.5 w-3.5" />
                            Reject documents
                          </Button>
                        </div>
                      ) : null}
                      {row.status === "rejected" || row.status === "approved" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => setConfirming({ row, action: "reopen" })}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Reopen for review
                        </Button>
                      ) : null}
                    </div>
                  )}
                </Td>
              </tr>
            )}
          />

          <Pagination
            currentPage={Number(list.data?.currentPage) || filters.page}
            totalPages={list.data?.totalPages ?? 1}
            totalRows={list.data?.totalRows ?? 0}
            pageSize={Number(filters.pageSize)}
            onPageChange={goToPage}
            label="submissions"
          />
        </>
      )}

      <FormDialog
        open={confirming?.action === "approve"}
        onOpenChange={(o) => !o && close()}
        title="Approve this identity?"
        description={`${confirming?.row?.username ?? ""} becomes verified.`}
        onSubmit={runApprove}
        submitLabel="Approve"
        loading={approve.isPending}
      />

      <FormDialog
        open={confirming?.action === "reopen"}
        onOpenChange={(o) => !o && close()}
        title="Reopen for review?"
        description={
          confirming?.row?.status === "approved"
            ? `${confirming?.row?.username ?? ""}'s verification is revoked and the submission goes back to pending. Withdrawals are paused until a new decision is made. They will be emailed.`
            : `${confirming?.row?.username ?? ""}'s submission goes back to pending. They will be emailed that the rejection was our error and that they should not upload again.`
        }
        onSubmit={runReopen}
        submitLabel="Reopen"
        loading={reopen.isPending}
      />

      <FormDialog
        open={confirming?.action === "supplement-approve"}
        onOpenChange={(o) => !o && close()}
        title="Accept these documents?"
        description={`${confirming?.row?.username ?? ""}'s proof of address and selfie are accepted and their file is complete. They were verified already and stay verified.`}
        onSubmit={() => runDecide("approved")}
        submitLabel="Accept"
        loading={decide.isPending}
      />

      {/* One dialog for both rejections: they differ in what they decide, not in how
          the reason is picked. */}
      <FormDialog
        open={confirming?.action === "reject" || supplementReject}
        onOpenChange={(o) => !o && close()}
        title={supplementReject ? "Reject documents — pick the reason" : "Reject — pick the reason"}
        description={
          supplementReject
            ? "This refuses the proof of address and selfie only. The user stays verified and can keep withdrawing; they are asked to send clearer copies. The wording below is what they receive."
            : "This wording is what the user receives, so keep it specific. On the selfie, the handwritten paper must show the user's name, their username as Account ID, and the date they submitted."
        }
        onSubmit={supplementReject ? () => runDecide("rejected") : runReject}
        submitLabel="Reject"
        submitDisabled={!rejectReason}
        loading={supplementReject ? decide.isPending : reject.isPending}
        danger
      >
        <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {reasons.map((r) => (
            <label
              key={r.value}
              className={
                "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors " +
                (reason === r.label
                  ? "border-ginox-green/60 bg-ginox-green/10 text-foreground"
                  : "border-border/70 bg-white/[0.02] text-muted-foreground hover:text-foreground")
              }
            >
              <input
                type="radio"
                name="rejection-reason"
                className="mt-1 accent-current"
                checked={reason === r.label}
                onChange={() => setReason(r.label)}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>

        {isOther ? (
          <Textarea
            autoFocus
            rows={3}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Write the reason the user will see…"
          />
        ) : null}
      </FormDialog>
    </QueueLayout>
  );
}
